import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Output, EventEmitter, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { CoreLogService } from "../../../core/services/log.service";
import { ISOTService } from '../../services';
export interface AccessibilityDialogConfig {
  sot_service: ISOTService;
}

interface AccessibilityContent {
  html: string;
  label: string;
  language: 'de' | 'en';
  sectionCount?: number;
}

@Component({
  selector: "accessibilityDialog",
  templateUrl: "./accessibilityDialog.component.html",
  styleUrls: ["./accessibilityDialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class AccessibilityDialogComponent implements OnInit {
  public dialogView: 'main' | 'accessibility' = 'main';
  public showAccessibilityText: boolean = false;
  public contentDE: AccessibilityContent = { html: '', label: '', language: 'de' };
  public contentEN: AccessibilityContent = { html: '', label: '', language: 'en' };
  public accessibilityHtml: string;
  public buttonBackText: string;
  public buttonMoreInfoText: string;
  private _istoggledModeOne: boolean;
  private _istoggledModeTwo: boolean;
  private _languageCache: { [key: string]: any } = {};

  public constructor(private _changeDetectorRef: ChangeDetectorRef
    , public dialogRef: MatDialogRef<AccessibilityDialogComponent, {}>
    , private _coreLogService: CoreLogService,
    @Inject(MAT_DIALOG_DATA) public accessibilityoptionsConfig: AccessibilityDialogConfig) {
    const me = this;
    me._istoggledModeOne = me.accessibilityoptionsConfig.sot_service.Accessabilty.useModeOne;
    me._istoggledModeTwo = me.accessibilityoptionsConfig.sot_service.Accessabilty.useModeTwo;
  }
  ngOnInit() {
    const me = this;
    me.initializeLanguageContent();
    me.setCurrentLanguageContent();
  }

  private initializeLanguageContent(): void {
    const me = this;

    try {
      const lngDE = me.getLanguageData('de');
      const lngEN = me.getLanguageData('en');

      me.contentDE = me.buildAccessibilityContent(lngDE, 'de');
      me.contentEN = me.buildAccessibilityContent(lngEN, 'en');
    } catch (error) {
      me._coreLogService.error('AccessibilityDialogComponent.initializeLanguageContent', 'Failed to load language files', error);
      me.contentDE = { html: '', label: '', language: 'de' };
      me.contentEN = { html: '', label: '', language: 'en' };
    }
  }

  private buildAccessibilityContent(languageData: any, language: 'de' | 'en'): AccessibilityContent {
    const me = this;
    const contentKeys: string[] = [
      "TITLE", "SECTION1_TITLE", "SECTION1_TEXT", "SECTION2_TITLE", "SECTION2_TEXT1", "SECTION2_TEXT2", "SECTION2_LIST",
      "SECTION3_TITLE", "SECTION3_TEXT", "SECTION4_TITLE", "SECTION4_TEXT1", "SECTION4_LIST1", "SECTION4_TEXT2", "SECTION4_LIST2",
      "SECTION4_SUBTITLE1", "SECTION4_SUBTITLE1_TEXT", "SECTION4_SUBTITLE2", "SECTION4_SUBTITLE2_TEXT",
      "SECTION4_SUBTITLE3", "SECTION4_SUBTITLE3_TEXT", "SECTION4_SUBTITLE4", "SECTION4_SUBTITLE4_TEXT",
      "SECTION5_TITLE", "SECTION5_TEXT1", "SECTION5_TEXT2", "SECTION5_LIST1", "SECTION5_TEXT3", "SECTION5_LIST2",
      "SECTION5_TEXT4", "SECTION5_LIST3", "SECTION5_TEXT5", "SECTION6_TITLE", "SECTION6_TEXT1", "SECTION6_LIST1",
      "SECTION6_TEXT2", "SECTION6_LIST2", "SECTION7_TITLE", "SECTION7_TEXT1", "SECTION7_CONTACT", "SECTION8_TITLE", "SECTION8_CONTACT"
    ];

    const getHtml = (lngObj: any, key: string): string => {
      const value = lngObj[`phTERMS_ACCESSIBILITY_${key}`];
      if (!value) return '';

      if (Array.isArray(value)) {
        return `<ul>${value.map(item => `<li>${item}</li>`).join('')}</ul>`;
      }
      if (key.includes('TITLE') && !key.includes('SUBTITLE')) {
        const level = key === 'TITLE' ? '1' : '2';
        return `<h${level}>${value}</h${level}>`;
      }
      if (key.includes('SUBTITLE') && !key.includes('_TEXT')) {
        return `<h3>${value}</h3>`;
      }
      if (key.includes('CONTACT')) {
        return `<p>${value.replace(/\n/g, '<br>')}</p>`;
      }
      return `<p>${value}</p>`;
    };

    const html: string = contentKeys.map(key => getHtml(languageData, key)).join('');
    const label: string = languageData["phTERMS_ACCESSIBILITY_TITLE"] || "";
    const sectionCount = contentKeys.filter(key => getHtml(languageData, key) !== '').length;

    return {
      html,
      label: label.replace(/<.*?>/g, ""),
      language,
      sectionCount
    };
  }

  private setCurrentLanguageContent(): void {
    const me = this;
    const currentLang = me.getCurrentLanguage();
    const content = currentLang === 'de' ? me.contentDE : me.contentEN;

    try {
      const lng = me.getLanguageData(currentLang);

      me.accessibilityHtml = content.html;
      me.buttonBackText = lng["phA11YDialog_Button_Back"] || (currentLang === 'de' ? "Zurück" : "Back");
      me.buttonMoreInfoText = lng["phA11YDialog_Button_MoreInfo"] || (currentLang === 'de' ? "Mehr Informationen" : "More Information");
    } catch (error) {
      me._coreLogService.error('AccessibilityDialogComponent.setCurrentLanguageContent', 'Failed to load language file', error);
      me.accessibilityHtml = content.html;
      me.buttonBackText = currentLang === 'de' ? "Zurück" : "Back";
      me.buttonMoreInfoText = currentLang === 'de' ? "Mehr Informationen" : "More Information";
    }
  }

  private getLanguageData(lang: string): any {
    const me = this;
    if (!me._languageCache[lang]) {
      try {
        me._languageCache[lang] = require(`../../../core/languages/${lang}.language`).lng;
      } catch (error) {
        me._coreLogService.error('AccessibilityDialogComponent.getLanguageData', `Failed to load ${lang} language file`, error);
        me._languageCache[lang] = {};
      }
    }
    return me._languageCache[lang];
  }

  private getCurrentLanguage(): string {
    const me = this;
    return me.accessibilityoptionsConfig.sot_service.GetCurrentLanguage && me.accessibilityoptionsConfig.sot_service.GetCurrentLanguage() || 'en';
  }

  public get isModeOne(): boolean {
    return this._istoggledModeOne;
  }

  public get isModeTwo(): boolean {
    return this._istoggledModeTwo;
  }

  private toggleMode(modeKey: '_istoggledModeOne' | '_istoggledModeTwo'): void {
    this[modeKey] = !this[modeKey];
    this.PushChanges();
  }


  public mode_one(): void {
    this.toggleMode('_istoggledModeOne');
  }

  public mode_two(): void {
    this.toggleMode('_istoggledModeTwo');
  }

  public setSelectedMode(): void {
    const me = this;
    me.accessibilityoptionsConfig.sot_service.Accessabilty.SetModeOne(me._istoggledModeOne);
    me.accessibilityoptionsConfig.sot_service.Accessabilty.SetModeTwo(me._istoggledModeTwo);
    me.dialogRef.close({ useModeOne: me._istoggledModeOne, useModeTwo: me._istoggledModeTwo });
  }
  public resetModes(): void {
    const me = this;
    me._istoggledModeOne = false;
    me._istoggledModeTwo = false;
    me.accessibilityoptionsConfig.sot_service.Accessabilty.SetModeOne(me._istoggledModeOne);
    me.accessibilityoptionsConfig.sot_service.Accessabilty.SetModeTwo(me._istoggledModeTwo);
    me.dialogRef.close({ useModeOne: me._istoggledModeOne, useModeTwo: me._istoggledModeTwo });
  }

  public closeAccessabiltydialog(): void {
    const me = this;
    me.dialogRef.close();
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("AccessibilityDialogComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }
}